import type { Company, CompanyAPIQueryParams, CompanyAPIBodyParams, CapitalStructure, QuoteHistory } from '~~/entities/company'
import type {
  APIQueryParams,
  PaginatedAPIDecorator,
  PaginatedAPIResults,
  APIJob,
  APIJobAPIBodyParams,
  APIJobQueryParams,
  APIJobFiles,
} from '~~/entities/api'
import type { User } from '~~/entities/user'
import { decoratePaginatedAPIResponse } from '~~/lib/utils'

export const SELECTED_COMPANY_ID_KEY = 'selectedCompanyID'

export function useCompanies() {
  const {
    listItemsPaginated: _listCompanies,
    getItem: getCompany,
    updateItem,
    deleteItem: deleteCompany,
    createItem: createCompany,
  } = useAPIClient<Company, CompanyAPIQueryParams, CompanyAPIBodyParams>('/v2/companies')
  const { $analytics } = useNuxtApp()

  const selectedCompany = useState<Company>('selectedCompany')
  const loading = ref(false)

  async function updateCompany(id: string | number, data: CompanyAPIBodyParams): Promise<void> {
    if (selectedCompany.value?.id === id) {
      selectedCompany.value = await updateItem(id, data)
    }
  }

  async function selectCompany(id: string | number) {
    selectedCompany.value = await getCompany(id, { include: ['last_quote'] })
    if (selectedCompany.value.id) {
      localStorage.setItem(SELECTED_COMPANY_ID_KEY, String(selectedCompany.value.id))
      $analytics.setCompany({
        companyId: `${selectedCompany.value.id}`,
        companyName: selectedCompany.value.name,
      })
    }
    return selectedCompany.value
  }

  async function getCompanyQuoteHistory(id: string | number): Promise<QuoteHistory> {
    return $fetch<QuoteHistory>(`/v2/companies/${id}/quote-history`, {
      ...useFetchSharedOptions(),
      method: 'GET',
    })
  }

  async function getCompanyCapitalStructure(id: string | number): Promise<CapitalStructure> {
    return $fetch<CapitalStructure>(`/v2/companies/${id}/capital-structure/latest`, {
      ...useFetchSharedOptions(),
      method: 'GET',
    })
  }

  async function getCompanyAdmins(id: string | number, params?: APIQueryParams): Promise<PaginatedAPIDecorator<User, APIQueryParams>> {
    return decoratePaginatedAPIResponse(
      await $fetch<PaginatedAPIResults<User>>(`/v2/companies/${id}/admins`, {
        ...useFetchSharedOptions(),
        method: 'GET',
        params,
      }),
    )
  }

  async function listCompanies(params?: CompanyAPIQueryParams): Promise<PaginatedAPIDecorator<Company, CompanyAPIQueryParams>> {
    try {
      loading.value = true
      return await _listCompanies(params)
    } finally {
      loading.value = false
    }
  }

  async function listJobs(params?: APIJobQueryParams): Promise<PaginatedAPIDecorator<APIJob, APIJobQueryParams>> {
    return decoratePaginatedAPIResponse(
      await $fetch<PaginatedAPIResults<APIJob>>(`/v2/companies/${selectedCompany.value?.id}/jobs`, {
        ...useFetchSharedOptions(),
        method: 'GET',
        params,
      }),
    )
  }

  async function getJob(companyId: string | number, jobId: string | number): Promise<APIJob> {
    return await $fetch<APIJob>(`/v2/companies/${companyId}/jobs/${jobId}`, {
      ...useFetchSharedOptions(),
      method: 'GET',
    })
  }

  async function createJob(companyId: string | number, data: APIJobAPIBodyParams): Promise<APIJob> {
    return $fetch<APIJob>(`/v2/companies/${companyId}/jobs`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: data,
    })
  }

  async function getJobFiles(jobId: string | number): Promise<APIJobFiles> {
    return await $fetch<APIJobFiles>(`/v2/companies/${selectedCompany.value?.id}/jobs/${jobId}/files`, {
      ...useFetchSharedOptions(),
      method: 'GET',
    })
  }

  return {
    loading: readonly(loading),
    listCompanies,
    getCompany,
    updateCompany,
    selectCompany,
    selectedCompany: readonly(selectedCompany),
    deleteCompany,
    createCompany,
    getCompanyQuoteHistory,
    getCompanyCapitalStructure,
    getCompanyAdmins,
    listJobs,
    getJob,
    createJob,
    getJobFiles,
  }
}
